import NotFound from "@/pages/NotFound";
import { lazy, Suspense } from "react";
import { Route, Switch } from "wouter";
import { RuntimeIssueReporter } from "./components/RuntimeIssueReporter";
import { TrackLabsLoader } from "./components/TrackLabsLoader";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import LoginPage from "./pages/Login";
import PublicLandingPage from "./pages/PublicLandingPage";

const TenantWorkspace = lazy(() => import("./pages/TenantWorkspace"));
const TenantCustomPageDetail = lazy(() => import("./pages/TenantCustomPageDetail"));
const AllLocationsPage = lazy(() => import("./pages/AllLocations"));
const StatisticsPage = lazy(() => import("./pages/Statistics"));
const TriggerEventLogPage = lazy(() => import("./pages/TriggerEventLog"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const RepairCenter = lazy(() => import("./pages/RepairCenter"));
const MapView = lazy(() => import("./pages/MapView"));
const ExcelImport = lazy(() => import("./pages/ExcelImport").then((m) => ({ default: m.ExcelImport })));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetTenantPassword = lazy(() => import("./pages/ResetTenantPassword"));
const UserHub = lazy(() => import("./pages/UserHub"));
const CustomPageDetail = lazy(() => import("./pages/CustomPageDetail"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const UserDirectory = lazy(() => import("./pages/UserDirectory"));
const ExportDownloads = lazy(() => import("./pages/ExportDownloads"));
const ScheduleDetail = lazy(() => import("./pages/ScheduleDetail"));
const PrintSignInSheet = lazy(() => import("./pages/PrintSignInSheet"));
const IdRequestPage = lazy(() => import("./pages/IdRequestPage").then((m) => ({ default: m.IdRequestPage })));
const TrackRequestPage = lazy(() => import("./pages/TrackRequestPage").then((m) => ({ default: m.TrackRequestPage })));
const VendorRequestPage = lazy(() => import("./pages/VendorRequestPage").then((m) => ({ default: m.VendorRequestPage })));
const OvertimeRegistration = lazy(() => import("./pages/OvertimeRegistration").then((m) => ({ default: m.OvertimeRegistration })));
const FormPage = lazy(() => import("./pages/FormPage").then((m) => ({ default: m.FormPage })));
const PublicPurchasePage = lazy(() => import("./pages/PublicPurchasePage"));
import { useSessionKeepAlive } from "./hooks/useSessionKeepAlive";
// Updated: Vendor request management system with dropdown field support
import { useSimpleInactivity } from "./hooks/useSimpleInactivity";
import { useLocation } from "wouter";
import { trpc } from "./lib/trpc";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { QueryMonitorDisplay } from "./components/QueryMonitor";
import { buildSessionExpiredLoginPath } from "@shared/session-expiration";
import FirstLoginSetup from "./pages/FirstLoginSetup";
import { CopyrightPage, TermsOfUsePage } from "./pages/LegalPage";
import { isPublicLaunchPath } from "./lib/desktopLaunch";

function TenantVendorFormRoute({ params }: any) {
  return <FormPage tenantSlug={params?.tenantSlug} formSlug={params?.formSlug} />;
}

function LegacyFormRoute({ params }: any) {
  return <FormPage formSlug={params?.slug} />;
}

function MainWorkspaceAccessGate() {
  const platformAccess = trpc.saas.getMyPlatformAccess.useQuery(undefined, { retry: false, refetchOnWindowFocus: false });

  if (platformAccess.isLoading) return <TrackLabsLoader label="Checking workspace access" />;
  if (platformAccess.data && !platformAccess.data.hasPlatformAccess && platformAccess.data.tenantSlug) {
    return <main className="min-h-screen grid place-items-center bg-background p-6 text-center"><div className="max-w-md rounded-3xl border border-border bg-card p-9 shadow-sm"><h1 className="text-2xl font-black text-foreground">Tenant workspace access</h1><p className="mt-3 text-sm leading-6 text-muted-foreground">This Site Admin account is assigned to a tenant workspace and does not have access to the Track Labs platform console.</p></div></main>;
  }
  return <UserHub />;
}

function Router() {
  const [location] = useLocation();
  const isPublicRoute = isPublicLaunchPath(location);

  // Public marketing/intake pages do not have an authenticated session to expire.
  // Keep the inactivity policy limited to authenticated workspace routes.
  useSessionKeepAlive(!isPublicRoute);

  const logoutMutation = trpc.auth.logout.useMutation();
  const tenantSlug = location.match(/^\/tenant\/([^/]+)(?:\/|$)/)?.[1];
  const redirectAfterSessionExpiration = () => {
    const returnPath = `${window.location.pathname}${window.location.search}`;
    window.location.href = buildSessionExpiredLoginPath(returnPath);
  };
  
  // Simple 5-minute inactivity logout
  useSimpleInactivity(() => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        // Clear all session data before redirect
        localStorage.clear();
        sessionStorage.clear();
        redirectAfterSessionExpiration();
      },
      onError: () => {
        // Even if logout fails, redirect to login
        localStorage.clear();
        sessionStorage.clear();
        redirectAfterSessionExpiration();
      },
    });
  }, tenantSlug, !isPublicRoute);
  
  return (
    <Suspense fallback={<TrackLabsLoader />}>
      <Switch>
      <Route path="/login" component={LoginPage} />
      <Route path="/terms-of-use" component={TermsOfUsePage} />
      <Route path="/copyright" component={CopyrightPage} />
      <Route path="/reset-password" component={ResetTenantPassword} />
      <Route path="/first-login" component={FirstLoginSetup} />
      <Route path="/purchase" component={PublicPurchasePage} />
      <Route path="/tenant/:tenantSlug/forms/:formSlug" component={TenantVendorFormRoute} />
      <Route path="/tenant/:tenantSlug/pages/:pageSlug" component={() => <ProtectedRoute><TenantCustomPageDetail /></ProtectedRoute>} />
      <Route path="/tenant/:slug/profile/:userId" component={() => <ProtectedRoute><UserProfile /></ProtectedRoute>} />
      <Route path="/tenant/:slug" component={TenantWorkspace} />
      <Route path={"/forgot-password"} component={ForgotPassword} />
      <Route path={"/app"} component={() => <ProtectedRoute><MainWorkspaceAccessGate /></ProtectedRoute>} />
      <Route path={"/repair-center"} component={() => <ProtectedRoute><RepairCenter /></ProtectedRoute>} />
      <Route path={"/all-locations"} component={() => <ProtectedRoute><AllLocationsPage /></ProtectedRoute>} />
      <Route path={"/statistics"} component={() => <ProtectedRoute><StatisticsPage /></ProtectedRoute>} />
      <Route path={"/trigger-log"} component={() => <ProtectedRoute><TriggerEventLogPage /></ProtectedRoute>} />
      <Route path={"/admin"} component={() => <ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
      <Route path={"/map"} component={() => <ProtectedRoute><MapView /></ProtectedRoute>} />
      <Route path={"/import"} component={() => <ProtectedRoute><ExcelImport /></ProtectedRoute>} />

      <Route path={"/leaderboard"} component={() => <ProtectedRoute><Leaderboard /></ProtectedRoute>} />
      <Route path={"/pages/:slug"} component={() => <CustomPageDetail />} />
      <Route path={"/profile/:userId"} component={() => <ProtectedRoute><UserProfile /></ProtectedRoute>} />
      <Route path={"/directory"} component={() => <ProtectedRoute><UserDirectory /></ProtectedRoute>} />

      <Route path={"/schedule/:id"} component={() => <ProtectedRoute><ScheduleDetail /></ProtectedRoute>} />
      <Route path={"/export-downloads"} component={() => <ProtectedRoute requireAdmin><ExportDownloads /></ProtectedRoute>} />
      <Route path={"/print-sign-in"} component={() => <ProtectedRoute><PrintSignInSheet /></ProtectedRoute>} />
      <Route path={"/request"} component={IdRequestPage} />
      <Route path={"/track-request"} component={TrackRequestPage} />
      <Route path={"/vendor-request"} component={VendorRequestPage} />
      <Route path={"/overtime-registration"} component={OvertimeRegistration} />
      <Route path={"/forms/:slug"} component={LegacyFormRoute} />
      <Route path={"/"} component={PublicLandingPage} />
      <Route path={"/404"} component={NotFound} />
      <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <RuntimeIssueReporter>
      <ThemeProvider
        defaultTheme="light"
        switchable
      >
        <TooltipProvider>
          <Router />
          <QueryMonitorDisplay />
        </TooltipProvider>
      </ThemeProvider>
    </RuntimeIssueReporter>
  );
}

export default App;
// Updated checkpoint
